<template>
  <div>
    <LibPageContainer>
      <LazyLibLocaleVersionOverlayContainer />
      <LazyDownloadsDocumentsDocumentCenterStandaloneModal
        v-if="mountDocumentCenterModal"
      />
      <LazyDownloadsSoftwareCenterStandaloneModal
        v-if="mountSoftwareCenterModal"
      />
      <LazyLibRegionSelector v-if="mountRegionSelector" />
      <ClientOnly>
        <OnboardingModalHost v-if="mountOnboardingHost" />
        <template #fallback></template>
      </ClientOnly>
      <slot />
    </LibPageContainer>
    <!--    <AudienceWidget />-->
  </div>
</template>

<script setup lang="ts">
import type { LOCALE_CODE } from '~~/lib/ContentfulService';
import { useGeoinformationStore } from '~/stores/geoinformationStore/geoinformationStore';
import { INJECTED_COLOR_MODE } from '~~/lib/constants';
import { useAppStore } from '~/stores/appStore/appStore';
import { useCustomerPreferencesStore } from '~/stores/customerPreferencesStore/customerPreferencesStore';
import buildFeatureToggles from '~/utils/buildFeatureToggles';

const OnboardingModalHost = defineAsyncComponent(
  () => import('~/components/onboarding/OnboardingModalHost.vue')
);

const route = useRoute();
const { $globalPageSettings, $locale } = useNuxtApp();
const { loggedIn } = useOidcAuth();
const { isOnboardingDebugActive } = useOnboardingDebug();
const customerPreferencesStore = useCustomerPreferencesStore();
const mountRegionSelector = ref(false);
const mountDocumentCenterModal = ref(false);
const mountSoftwareCenterModal = ref(false);
const mountOnboardingHost = ref(false);

const shouldMountOnboardingHost = computed(() => {
  const featureToggles = buildFeatureToggles($globalPageSettings.value);
  const enabledLocales = featureToggles?.showOnboardingModal ?? [];

  return (
    isOnboardingDebugActive.value ||
    (loggedIn.value && enabledLocales.includes($locale.value))
  );
});

watch(
  shouldMountOnboardingHost,
  (shouldMount: boolean) => {
    if (shouldMount) {
      mountOnboardingHost.value = true;
    }
  },
  { immediate: true }
);

watch(
  [loggedIn, shouldMountOnboardingHost],
  ([authenticated, shouldLoad]: [boolean, boolean]) => {
    if (!import.meta.client) {
      return;
    }

    if (!authenticated) {
      customerPreferencesStore.reset();
      return;
    }

    if (shouldLoad) {
      void customerPreferencesStore.fetchPreferences();
    }
  },
  { immediate: true }
);

watch(
  () => route.hash,
  (hash) => {
    if (hash === '#regionselector') {
      mountRegionSelector.value = true;
    }
    if (hash?.includes('download=')) {
      mountDocumentCenterModal.value = true;
    }
    if (hash?.includes('software=')) {
      mountSoftwareCenterModal.value = true;
    }
  },
  { immediate: true }
);

const appStore = useAppStore();
appStore.initialize();

const locale = useLocale();
const { $colorMode } = useNuxtApp();
const geoInformationStore = useGeoinformationStore();
const logger = useLogger();

useGlobalMetadata();

onMounted(() => {
  // load initial geo information only on client side once inside layout
  geoInformationStore.init().catch(logger.error);
});

const isAsian = computed(() => {
  const asianRegions: Lowercase<LOCALE_CODE>[] = [
    'zh-cn',
    'zh-hant-tw',
    'ja-jp',
    'ko-kr'
  ];

  return asianRegions.includes(locale.value as Lowercase<LOCALE_CODE>);
});
const styles = useCssModule();
// Using state values is possible inside head but not on component used in
// layout (layout is rendered before actual slot components which
// can manipulate state. Even though state changes, layout is not updated.
useHead({
  bodyAttrs: {
    class: () => ({
      'is-region-asian': isAsian.value,
      'is-region-western': !isAsian.value,
      [styles.dark]: $colorMode.value === 'dark',
      [styles.light]: $colorMode.value === 'light',
      [styles['dark-purple']]: $colorMode.value === 'dark-purple'
    })
  },
  // DBP-4325: preload LCP-critical fonts for the active region (roman only; italics are Tier 2)
  link: () =>
    isAsian.value
      ? [
          {
            rel: 'preload',
            as: 'font',
            type: 'font/woff2',
            crossorigin: '',
            href: '/fonts/DINWebPro-Light.asian.subset.woff2'
          },
          {
            rel: 'preload',
            as: 'font',
            type: 'font/woff2',
            crossorigin: '',
            href: '/fonts/DINWebPro.asian.subset.woff2'
          },
          {
            rel: 'preload',
            as: 'font',
            type: 'font/woff2',
            crossorigin: '',
            href: '/fonts/DINWebPro-Medium.asian.subset.woff2'
          }
        ]
      : [
          {
            rel: 'preload',
            as: 'font',
            type: 'font/woff2',
            crossorigin: '',
            href: '/fonts/Inter-roman.var.latinext.subset.woff2'
          },
          {
            rel: 'preload',
            as: 'font',
            type: 'font/woff2',
            crossorigin: '',
            href: '/fonts/Montserrat-VF.latinext.subset.woff2'
          }
        ]
});

watch(
  () => $colorMode.value,
  () => {
    provide(INJECTED_COLOR_MODE, readonly($colorMode));
  },
  { immediate: true }
);
</script>

<style module>
.light {
  --app-canvas-effect-opacity: 1;
  --app-canvas-color-canvas: #002a95;
}

.dark {
  --app-canvas-effect-opacity: 0;
  --app-canvas-color-canvas: #002a95;
}

.dark-purple {
  --app-canvas-effect-opacity: 0;
  --app-canvas-color-canvas: #38068a;
}
</style>
